// ============================================================================

import {isCanonicalCommentAnchorHash} from './commentAnchorHash.ts';
// Canonical Tasks record route contracts — To Do detail AND task instance
// ============================================================================
//
// The two record surfaces under TasksRouter are written in ONE module on
// purpose. `/tasks/*` is a single splat row, and the router picks between four
// shapes on the RAW pathname alone:
//
//   /tasks              -> tasks.center
//   /tasks/todo         -> tasks.center      (exact, and its trailing slash)
//   /tasks/todo/<id>    -> tasks.todo.detail
//   /tasks/<id>         -> tasks.instance.detail
//
// So the instance matcher's correctness is a statement ABOUT the To Do prefix,
// and the To Do matcher's correctness is a statement about not being swallowed
// by the instance one. Splitting them across two modules would mean writing an
// exclusion in one file that only the other file's contents make true.
//
// Neither predicate knows about the ledger, the renderer, or any data owner.
// Their only job is to keep each independently reviewed record form from
// drifting as wide as the historical router, which deliberately accepts every
// non-empty suffix. Wider raw shapes receive explicit route recovery.
//
// RESERVED SEGMENTS
// `src/lib/routes.ts` registers exactly one `/tasks` path (`VIEW_TO_PATH.tasks`)
// and no `/tasks/<segment>` route, alias source, alias target, or prefix alias
// at all — `/my-tasks` and `/admin/tasks` are alias SOURCES that resolve TO
// `/tasks`, and `/dailys/tasks` is a different tree. The only reserved segment
// beneath `/tasks/` therefore comes from TasksRouter's own branch order, and it
// is `todo`.
// ============================================================================

export const TASKS_TODO_DETAIL_PATHNAME_PREFIX = '/tasks/todo/';
export const TASKS_INSTANCE_DETAIL_PATHNAME_PREFIX = '/tasks/';

// `todo` is TasksRouter's own namespace, not a task id. It is checked against
// the DECODED segment as well as the raw one, so `/tasks/%74odo` cannot enter
// the instance release through an encoding that the router itself does not
// decode.
const RESERVED_TASKS_INSTANCE_SEGMENTS: ReadonlySet<string> = new Set(['todo']);

type LocationLike = Readonly<{pathname?: unknown; search?: unknown; hash?: unknown}>;

// One meaningful raw segment: no separator in either direction, no NUL, no line
// break, and no query/fragment marker smuggled into a bare pathname string.
function isSafeRawSegment(rawSegment: string): boolean {
  return !(
    rawSegment === '' ||
    rawSegment.includes('/') ||
    rawSegment.includes('\\') ||
    rawSegment.includes('\0') ||
    rawSegment.includes('\r') ||
    rawSegment.includes('\n') ||
    rawSegment.includes('?') ||
    rawSegment.includes('#')
  );
}

// The same rules again after ONE decode, which is what turns `%2F`, `%5C`,
// `%00`, `%0A`, `%0D`, `%2E`, and `%2E%2E` into rejections rather than into
// accepted opaque text. A malformed escape makes decodeURIComponent throw and
// the caller's catch returns false.
function isSafeDecodedSegment(segment: string): boolean {
  return (
    segment.trim() !== '' &&
    segment !== '.' &&
    segment !== '..' &&
    !segment.includes('/') &&
    !segment.includes('\\') &&
    !segment.includes('\0') &&
    !segment.includes('\r') &&
    !segment.includes('\n') &&
    !segment.includes('?') &&
    !segment.includes('#')
  );
}

function soleSegmentAfter(pathname: unknown, prefix: string): string | null {
  if (typeof pathname !== 'string' || !pathname.startsWith(prefix)) return null;
  const rawSegment = pathname.slice(prefix.length);
  if (!isSafeRawSegment(rawSegment)) return null;
  try {
    const segment = decodeURIComponent(rawSegment);
    return isSafeDecodedSegment(segment) ? segment : null;
  } catch {
    return null;
  }
}

/**
 * `/tasks/todo/<id>` with exactly one decoded-safe segment.
 *
 * `/tasks/todo` and `/tasks/todo/` do not start with the prefix and end at it
 * respectively, so this predicate cannot claim either Task Center form.
 */
export function isCanonicalTasksTodoDetailPathname(pathname: unknown): pathname is string {
  return soleSegmentAfter(pathname, TASKS_TODO_DETAIL_PATHNAME_PREFIX) !== null;
}

/**
 * `/tasks/<id>` with exactly one decoded-safe, non-reserved segment.
 *
 * The To Do namespace is excluded twice over: `todo` is reserved, and
 * `/tasks/todo/<id>` leaves `todo/<id>` in this position, which carries a
 * separator and is refused before any decode happens.
 */
export function isCanonicalTasksInstanceDetailPathname(pathname: unknown): pathname is string {
  const segment = soleSegmentAfter(pathname, TASKS_INSTANCE_DETAIL_PATHNAME_PREFIX);
  return segment !== null && !RESERVED_TASKS_INSTANCE_SEGMENTS.has(segment);
}

// React Router supplies pathname/search/hash separately. A bare string is
// accepted only when it is plainly a pathname, so a caller or test seam cannot
// smuggle an unsupported query or fragment into a released target.
//
// Both record pages read `location.hash` themselves to scroll a `#comment-<id>`
// mention deep-link into view, so that one canonical fragment remains part of
// the audited record render. TasksRouter reads `?task=` on `/tasks`, but record
// targets do not: any search carrier, arbitrary non-empty hash, or malformed
// raw pathname stays outside the released claim and receives explicit recovery.
function isCanonicalRecordLocation(location: unknown, matches: (pathname: unknown) => boolean): boolean {
  if (typeof location === 'string') return matches(location);
  if (!location || typeof location !== 'object' || Array.isArray(location)) return false;

  const {pathname, search, hash} = location as LocationLike;
  return (
    (search === undefined || search === '') &&
    (hash === undefined || hash === '' || isCanonicalCommentAnchorHash(hash)) &&
    matches(pathname)
  );
}

export function isCanonicalTasksTodoDetailLocation(location: unknown): boolean {
  return isCanonicalRecordLocation(location, isCanonicalTasksTodoDetailPathname);
}

export function isCanonicalTasksInstanceDetailLocation(location: unknown): boolean {
  return isCanonicalRecordLocation(location, isCanonicalTasksInstanceDetailPathname);
}
